import { useMemo, type FC } from 'react';
import { Link } from 'react-router-dom';

import styles from '../home.module.css';
import type { HomeViewProps } from '../home.types';
import clsx from 'clsx';
import { Analytics } from 'lib/AnalyticsLoader/analytics';

import { MINIMUM_POINTS_TO_PLAY } from 'modules/prize-box-page/prize-box-page.constants';

import DesktopLayout from 'shared/components/layout/desktop-layout';
import { Avatar } from 'shared/components/ui/Avatar/Avatar';
import { OptimizedImage } from 'shared/components/ui/optimized-image/optimized-image';
import { InfiniteMaxProgressBar } from 'shared/components/ui/progress-bar/progress-bar';
import { ROUTES } from 'shared/constants';
import { DISCORD_USERNAME_LENGTH } from 'shared/constants/constants';
import { SOCIAL_MEDIA } from 'shared/constants/socialMedia';
import { useGlobalStore } from 'shared/store/globalStore';
import { prettyTx } from 'shared/utils/pretty-tx';

import ArrowDown from 'assets/arrow.svg?react';
import ImageJoinDiscord from 'assets/landing/discord.png?format=webp;avif&as=picture';
import ImageLeaderBoard from 'assets/landing/leaderboard.png?format=webp;avif&as=picture';
import ImageNFTs from 'assets/landing/nfts.png?format=webp;avif&as=picture';
import ImagePlayGame from 'assets/landing/playgame.png?format=webp;avif&as=picture';
import ImagePrizeBox from 'assets/landing/prizebox.png?format=webp;avif&as=picture';
import ImageQuests from 'assets/landing/quests.png?format=webp;avif&as=picture';
import attentionBig from 'assets/png/attentionBig.png?format=webp;avif&as=picture';

export const DesktopView: FC<HomeViewProps> = ({
  title,
  chooseText,
  yourChainText,
  address,
  pointBalance,
  attemptsLeft,
  activeQuests,
  currentChain,
  chains,
  networkOpen,
  isConnected,
  setNetworkOpen,
  onChainSelect,
}) => {
  const leaderboardMap = useGlobalStore((state) => state.leaderboardMap);
  const screenAddress = useMemo(
    () =>
      prettyTx({
        address,
        leaderboardMap,
        discordUsernameLength: DISCORD_USERNAME_LENGTH,
      }),
    [address, leaderboardMap],
  );
  return (
    <DesktopLayout title={title}>
      <div className="flex flex-col items-stretch justify-end gap-6 pb-3 lg:flex-row lg:justify-between xlg:gap-8">
        <div className="space-y-3">
          {isConnected && (
            <div className="mb-16 flex w-full flex-col items-center justify-between gap-6 xl:flex-row">
              <div className="flex w-full items-center justify-between gap-4 rounded-l-2xl rounded-r-[50px] border border-border bg-gradient-to-b from-grape to-[#030220] p-1.5 pl-2 pr-3 md:w-1/2 2xl:w-1/3">
                <div className="flex items-center gap-4">
                  <Avatar
                    userAddress={address?.toLowerCase() as `0x${string}`}
                    className="h-16 w-16 rounded-2xl"
                  />
                  <p className="text-xl font-semibold text-orange-btn">{screenAddress}</p>
                </div>
                <InfiniteMaxProgressBar
                  value={Number(pointBalance)}
                  showAttention
                  className={clsx({
                    'cursor-pointer': pointBalance > MINIMUM_POINTS_TO_PLAY,
                  })}
                />
              </div>

              <div className="flex w-full items-center justify-between gap-5 rounded-3xl border border-border bg-gradient-to-b from-one-more-blue to-[#0F0AAB] p-1.5 pl-10 md:w-1/2 xl:w-auto 2xl:gap-10">
                <div>
                  <p className={clsx(styles['text-gradient-stroke'], 'text-3xl')}>{chooseText}</p>
                  <p className={clsx(styles['text-gradient-stroke'], 'text-nowrap text-xl')}>
                    {yourChainText}
                  </p>
                </div>

                <div className="relative flex flex-col items-center justify-center gap-1 rounded-2xl bg-move-icon-border p-[1px]">
                  <div className="min-w-80 overflow-hidden rounded-2xl bg-[linear-gradient(180deg,_#0D1450_0%,_#172077_100%)] p-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setNetworkOpen(!networkOpen);
                      }}
                      className="flex w-full items-center justify-between gap-2.5 pr-2 text-white"
                    >
                      <span className="flex items-center gap-2 text-2xl font-semibold leading-3 text-white">
                        {currentChain?.icon && (
                          <div className="flex items-center justify-center rounded-xl bg-[#12AAFF] p-1.5">
                            <img src={currentChain.icon} className="h-8 w-8 rounded-full" />
                          </div>
                        )}
                        {currentChain?.name ?? '—'}
                      </span>
                      <ArrowDown
                        className={clsx(
                          { 'rotate-180 transform': networkOpen },
                          'w-6 transition duration-500 ease-in-out xl:ml-3 2xl:ml-6',
                        )}
                      />
                    </button>

                    {networkOpen && (
                      <div className="absolute -right-1 z-10 mt-2 w-52 rounded-lg bg-[#0D1450] p-2 shadow-lg">
                        {chains.map((c) => (
                          <button
                            key={c.id}
                            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-white hover:bg-white/10"
                            onClick={() => onChainSelect(c.id)}
                          >
                            {c.icon && <img src={c.icon} className="h-5 w-5 rounded-full" />}
                            <span>{c.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-row items-stretch justify-between gap-4 2xl:gap-8">
            <div className="flex w-full flex-col items-center justify-center gap-6">
              <Link
                to={ROUTES.LOBBY}
                onClick={() => Analytics.homePageItemClickEvent('Lobby Link Click')}
                className="flex w-full transition-all duration-300 hover:scale-105"
              >
                <OptimizedImage
                  imageData={ImagePlayGame}
                  alt="play game"
                  className="w-full"
                  data-testid="img:play game"
                />
              </Link>
              <div className="flex items-start justify-center gap-6">
                <a
                  href={SOCIAL_MEDIA.DISCORD}
                  onClick={() => Analytics.homePageItemClickEvent('Discord Link Click')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex w-full transition-all duration-300 hover:scale-105"
                >
                  <OptimizedImage
                    imageData={ImageJoinDiscord}
                    alt="join discord"
                    className="w-full"
                  />
                </a>
                <Link
                  to={ROUTES.PRIZE_BOX}
                  onClick={() => Analytics.homePageItemClickEvent('PrizeBox Link Click')}
                  className="relative flex w-full transition-all duration-300 hover:scale-105"
                >
                  <OptimizedImage
                    imageData={ImagePrizeBox}
                    alt="prize box"
                    className="w-full"
                    data-testid="img:prize box"
                  />
                  {attemptsLeft > 0 && (
                    <div
                      className={clsx(
                        styles.circle,
                        'absolute -bottom-1 -right-1 h-16 w-16 rounded-full text-5xl',
                      )}
                    >
                      {attemptsLeft}
                    </div>
                  )}
                </Link>
              </div>
            </div>

            <div className="my-8 w-[6px] flex-shrink-0 self-stretch rounded-lg bg-indigo" />

            <div className="flex w-full flex-col items-center justify-center gap-6">
              <div className="flex items-center justify-center gap-6">
                <Link
                  to={ROUTES.DAILY_QUESTS}
                  onClick={() => Analytics.homePageItemClickEvent('Quests Link Click')}
                  className="relative flex w-full transition-all duration-300 hover:scale-105"
                >
                  <OptimizedImage imageData={ImageQuests} alt="quests" className="w-full" />
                  {activeQuests > 0 && (
                    <OptimizedImage
                      imageData={attentionBig}
                      alt="attention"
                      className="absolute -bottom-1 -right-1 h-16 w-16 animate-badgePop motion-reduce:animate-none"
                    />
                  )}
                </Link>
                <Link
                  to={ROUTES.NFTS}
                  onClick={() => Analytics.homePageItemClickEvent('NFT Link Click')}
                  className="relative flex w-full transition-all duration-300 hover:scale-105"
                >
                  <OptimizedImage imageData={ImageNFTs} alt="nfts" className="w-full" />
                  {Number(pointBalance) >= 100 && (
                    <OptimizedImage
                      imageData={attentionBig}
                      alt="attention"
                      className="absolute -bottom-1 -right-1 h-16 w-16 animate-badgePop motion-reduce:animate-none"
                    />
                  )}
                </Link>
              </div>
              <Link
                to={ROUTES.LEADERBOARD}
                onClick={() => Analytics.homePageItemClickEvent('Leaderboard Link Click')}
                className="flex w-full transition-all duration-300 hover:scale-105"
              >
                <OptimizedImage imageData={ImageLeaderBoard} alt="leaderboard" className="w-full" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </DesktopLayout>
  );
};
